/** How much of a file its documents cover: pages for a PDF, sheets for a workbook. */
export interface FileCoverage {
    unit: 'pages' | 'sheets';
    total: number;
    /** Pages in no document, or sheets with no answer. */
    missing: number;
    /** Nothing has been split yet. */
    empty: boolean;
}

/** Fields filled of those the document's type asks for. */
export interface FieldsProgress {
    filled: number;
    total: number;
}

/** Step 1's chip status, e.g. "585 pp · ✓" or "3 pp missing". */
export function fileStepText(coverage: FileCoverage | null): string {
    if (coverage === null) return '…';
    const open = coverage.empty ? coverage.total : coverage.missing;
    if (coverage.unit === 'pages') {
        return open === 0
            ? `${coverage.total} pp · ✓`
            : `${open} pp missing`;
    }
    return open === 0 ? `${coverage.total} sheets · ✓` : `${open} unanswered`;
}

/** Step 2's chip status, e.g. "3/5 verified". */
export function documentsStepText(
    documentCount: number,
    verifiedCount: number,
): string {
    return `${verifiedCount}/${documentCount} verified`;
}

/** Step 3's chip status, counting only documents whose type has fields. */
export function fieldsStepText(documents: FieldsProgress[]): string {
    const withFields = documents.filter((d) => d.total > 0);
    if (withFields.length === 0) return '–';
    const done = withFields.filter((d) => d.filled >= d.total).length;
    return `${done}/${withFields.length} filled`;
}

export interface FieldsLineState {
    text: string;
    tone: 'neutral' | 'warning' | 'success';
    /** Shows the progress bar and the link to the document page. */
    canOpen: boolean;
    percent: number;
    linkLabel: 'Open fields' | 'Fill fields';
}

/** What a document row's "3 Fields" line says, from whether its document exists, is stale, and how many fields are filled. */
export function fieldsLineState({
    hasDocument,
    stale,
    filled,
    total,
}: {
    hasDocument: boolean;
    stale: boolean;
    filled: number;
    total: number;
}): FieldsLineState {
    const base = {
        tone: 'neutral' as const,
        canOpen: false,
        percent: 0,
        linkLabel: 'Open fields' as const,
    };
    if (!hasDocument) {
        return { ...base, text: 'Fields open after you press Create documents' };
    }
    if (stale) {
        return {
            ...base,
            tone: 'warning',
            text: 'Pages changed. Press Create documents again to fill fields',
        };
    }
    if (total === 0) return { ...base, text: 'No fields for this type' };
    const percent = Math.round((100 * Math.min(filled, total)) / total);
    const open = {
        ...base,
        canOpen: true,
        percent,
        linkLabel: filled === 0 ? ('Fill fields' as const) : ('Open fields' as const),
    };
    if (filled >= total) {
        return {
            ...open,
            tone: 'success',
            text: `✓ Fields ${total} of ${total} filled`,
        };
    }
    if (filled === 0) {
        return { ...open, text: `Fields not started (0 of ${total})` };
    }
    return { ...open, text: `Fields ${filled} of ${total} filled` };
}

/** A chip status in plain words for a tooltip: "585 pp · ✓" becomes "585 pages, all covered". */
export function plainStatusText(status: string): string {
    if (status === '–') return 'nothing to fill yet';
    if (status === '…') return 'loading';
    return status
        .replace(/(\d+) pp · ✓/, '$1 pages, all covered')
        .replace(/(\d+) sheets · ✓/, '$1 sheets, all answered')
        .replace(/(\d+) pp missing/, '$1 pages not in any document')
        .replace(/(\d+) unanswered/, '$1 sheets with no answer');
}
